import * as React from 'react';

import useResizeObserver from 'use-resize-observer';

import { Row } from '../containers';

type MaybeTruncatedListProps<T> = {
  items: T[];
  renderItem: (item: T, index: number) => React.ReactNode;
  renderPlus: (collapsedItems: T[]) => React.ReactNode;
};

export const MaybeTruncatedList = <T,>({
  items,
  renderItem,
  renderPlus,
}: MaybeTruncatedListProps<T>) => {
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const itemRefs = React.useRef<(HTMLDivElement | null)[]>([]);
  const plusRef = React.useRef<HTMLDivElement | null>(null);

  const { width: containerWidth } = useResizeObserver<HTMLDivElement>({
    ref: containerRef as React.RefObject<HTMLDivElement>,
  });

  const [visibleCount, setVisibleCount] = React.useState(items.length);
  const [itemWidths, setItemWidths] = React.useState<number[]>([]);

  // Observe each item's width changes
  React.useLayoutEffect(() => {
    if (items.length === 0) {
      setItemWidths([]);
      return;
    }

    const observers: ResizeObserver[] = [];
    const widths: number[] = new Array(items.length).fill(0);

    const updateWidths = () => {
      setItemWidths([...widths]);
    };

    // Set up ResizeObserver for each item
    for (let i = 0; i < items.length; i++) {
      const element = itemRefs.current[i];
      if (!element) {
        // Element not ready yet, measure directly
        requestAnimationFrame(() => {
          const el = itemRefs.current[i];
          if (el) {
            widths[i] = el.offsetWidth;
            updateWidths();
          }
        });
        continue;
      }

      widths[i] = element.offsetWidth;
      const observer = new ResizeObserver(() => {
        const el = itemRefs.current[i];
        if (el) {
          widths[i] = el.offsetWidth;
          updateWidths();
        }
      });
      observer.observe(element);
      observers.push(observer);
    }

    updateWidths();

    return () => {
      observers.forEach((observer) => observer.disconnect());
    };
  }, [items.length]);

  // Recalculate visible count when container width or item widths change
  React.useLayoutEffect(() => {
    if (!containerWidth || items.length === 0) {
      setVisibleCount(items.length);
      return;
    }

    if (itemWidths.length !== items.length) {
      // Still measuring, wait
      return;
    }

    const calculateVisibleCount = () => {
      const gap = 0; // No gap since spacing is handled by renderItem (commas)

      // Measure plus element (using upper bound for conservative estimate)
      const plusWidth = plusRef.current?.offsetWidth ?? 0;

      // Try to fit items one by one, accounting for plus element when needed
      let totalWidth = 0;
      for (let i = 0; i < items.length; i++) {
        const wouldNeedPlus = i < items.length - 1;
        const neededWidth =
          itemWidths[i] + (wouldNeedPlus ? plusWidth + gap : 0) + gap;

        if (totalWidth + neededWidth > containerWidth) {
          setVisibleCount(i);
          return;
        }

        totalWidth += itemWidths[i] + gap;
      }

      // All items fit
      setVisibleCount(items.length);
    };

    // Use requestAnimationFrame to ensure DOM has updated
    requestAnimationFrame(calculateVisibleCount);
  }, [containerWidth, items.length, itemWidths]);

  const collapsedItems = items.slice(visibleCount);

  return (
    <Row ref={containerRef} className="@MaybeTruncatedList w-full">
      {/* Render all items invisibly for measurement, then show only visible ones */}
      {items.map((item, index) => {
        const isVisible = index < visibleCount;
        return (
          <div
            key={index}
            ref={(el) => {
              itemRefs.current[index] = el;
            }}
            style={{
              visibility: isVisible ? 'visible' : 'hidden',
              position: isVisible ? undefined : 'absolute',
            }}
          >
            {renderItem(item, index)}
          </div>
        );
      })}
      {/* Always render plus element invisibly for measurement, show when needed */}
      <div ref={plusRef} className={'ml-1.5'}>
        {/* Render with actual count when visible, or upper bound for measurement */}
        {renderPlus(collapsedItems)}
      </div>
    </Row>
  );
};
